import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../../contexts/AuthContext';
import { ArrowLeft, Printer } from 'lucide-react';
import BlurredValue from '../../../components/common/BlurredValue';
import { formatDateToISO } from '../../../utils/formatters';
import LoadingSpinner from '../../../components/common/LoadingSpinner';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

const WholesaleCashFlowPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [period, setPeriod] = useState('this_month');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');

  useEffect(() => {
    if (period !== 'custom') {
      fetchReport();
    }
  }, [period]);

  const fetchReport = async () => {
    try {
      setLoading(true);
      setError('');
      const token = localStorage.getItem('accessToken');

      let startDate, endDate;

      if (period === 'custom') {
        if (!customStartDate || !customEndDate) {
          setError('Please select both start and end dates');
          setLoading(false);
          return;
        }
        startDate = customStartDate;
        endDate = customEndDate;
      } else {
        const today = new Date();
        endDate = formatDateToISO(today);

        switch (period) {
          case 'this_month':
            const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
            startDate = formatDateToISO(monthStart);
            break;
          case 'last_month':
            const lastMonthStart = new Date(today.getFullYear(), today.getMonth() - 1, 1);
            const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);
            startDate = formatDateToISO(lastMonthStart);
            endDate = formatDateToISO(lastMonthEnd);
            break;
          case 'this_quarter':
            const quarter = Math.floor(today.getMonth() / 3);
            const quarterStart = new Date(today.getFullYear(), quarter * 3, 1);
            startDate = formatDateToISO(quarterStart);
            break;
          case 'this_year':
            const yearStart = new Date(today.getFullYear(), 0, 1);
            startDate = formatDateToISO(yearStart);
            break;
          default:
            startDate = formatDateToISO(new Date(today.getFullYear(), today.getMonth(), 1));
        }
      }

      const url = `${API_URL}/reports/cash-flow?start_date=${startDate}&end_date=${endDate}&module=wholesale`;

      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setReportData(response.data?.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch wholesale cash flow statement');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  const handlePrint = () => {
    window.print();
  };

  const renderAmount = (amount) => {
    const isNegative = amount < 0;
    return (
      <span className={isNegative ? 'text-red-600' : 'text-gray-900'}>
        {isNegative && '('}
        {formatCurrency(Math.abs(amount))}
        {isNegative && ')'}
      </span>
    );
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="p-4 md:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4 print:hidden">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/wholesale/reports')}
              className="p-2 hover:bg-gray-100 rounded-lg"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Wholesale Cash Flow Statement</h1>
              <p className="text-sm text-gray-500">Statement of Cash Flows - Wholesale Module</p>
            </div>
          </div>
          <button
            onClick={handlePrint}
            className="text-white px-4 py-2 rounded-lg flex items-center gap-2"
            style={{ backgroundColor: 'var(--wholesale-primary, #047857)' }}
          >
            <Printer className="h-4 w-4" />
            Print
          </button>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow p-6 mb-6 print:hidden">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Period</label>
              <select
                value={period}
                onChange={(e) => setPeriod(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              >
                <option value="this_month">This Month</option>
                <option value="last_month">Last Month</option>
                <option value="this_quarter">This Quarter</option>
                <option value="this_year">This Year</option>
                <option value="custom">Custom Range</option>
              </select>
            </div>

            {period === 'custom' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                  <input
                    type="date"
                    value={customStartDate}
                    onChange={(e) => setCustomStartDate(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-emerald-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                  <input
                    type="date"
                    value={customEndDate}
                    onChange={(e) => setCustomEndDate(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-emerald-500"
                  />
                </div>
              </>
            )}

            <div className="flex items-end">
              <button
                onClick={fetchReport}
                className="w-full text-white px-4 py-2 rounded-lg font-medium"
                style={{ backgroundColor: 'var(--wholesale-primary, #047857)' }}
              >
                Generate Report
              </button>
            </div>
          </div>
        </div>

        {/* Print Header */}
        <div className="hidden print:block mb-6">
          <h1 className="text-2xl font-bold text-gray-900 text-center">
            Wholesale Cash Flow Statement
          </h1>
          <p className="text-sm text-gray-600 text-center">{reportData?.period?.label}</p>
          <p className="text-sm text-gray-600 text-center">Wholesale Module Report</p>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-6 print:hidden">
            {error}
          </div>
        )}

        {reportData && (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6 print:hidden">
              <div className="bg-white rounded-lg shadow p-4 border-2 border-gray-500">
                <div className="text-sm text-gray-500">Beginning Cash</div>
                <div className="text-xl md:text-2xl lg:text-3xl font-bold text-gray-900">
                  <BlurredValue value={formatCurrency(reportData.beginning_cash)} />
                </div>
              </div>

              <div className="bg-white rounded-lg shadow p-4 border-2 border-emerald-500">
                <div className="text-sm text-gray-500">Operating Activities</div>
                <div
                  className={`text-xl md:text-2xl lg:text-3xl font-bold ${reportData.operating_activities.net_cash >= 0 ? 'text-green-600' : 'text-red-600'}`}
                >
                  <BlurredValue value={formatCurrency(reportData.operating_activities.net_cash)} />
                </div>
              </div>

              <div className="bg-white rounded-lg shadow p-4 border-2 border-blue-500">
                <div className="text-sm text-gray-500">Net Change</div>
                <div
                  className={`text-xl md:text-2xl lg:text-3xl font-bold ${reportData.net_change_in_cash >= 0 ? 'text-green-600' : 'text-red-600'}`}
                >
                  <BlurredValue value={formatCurrency(reportData.net_change_in_cash)} />
                </div>
              </div>

              <div className="bg-white rounded-lg shadow p-4 border-2 border-amber-500">
                <div className="text-sm text-gray-500">Ending Cash</div>
                <div className="text-xl md:text-2xl lg:text-3xl font-bold text-emerald-600">
                  <BlurredValue value={formatCurrency(reportData.ending_cash)} />
                </div>
              </div>
            </div>

            {/* Cash Flow Report */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Item
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                        Amount
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {/* Beginning Cash */}
                    <tr className="bg-gray-100">
                      <td className="px-6 py-4 text-sm font-bold text-gray-900">
                        Beginning Cash Balance
                      </td>
                      <td className="px-6 py-4 text-sm text-right font-bold text-gray-900">
                        {formatCurrency(reportData.beginning_cash)}
                      </td>
                    </tr>

                    {/* Operating Activities */}
                    <tr className="bg-emerald-50">
                      <td colSpan={2} className="px-6 py-3 text-sm font-bold text-gray-900">
                        CASH FROM OPERATING ACTIVITIES
                      </td>
                    </tr>
                    {reportData.operating_activities.breakdown.map((item, idx) => (
                      <tr key={idx}>
                        <td className="px-6 py-3 text-sm text-gray-900 pl-10">{item.item}</td>
                        <td className="px-6 py-3 text-sm text-right">
                          {renderAmount(item.amount)}
                        </td>
                      </tr>
                    ))}
                    <tr className="font-bold bg-green-50">
                      <td className="px-6 py-4 text-sm text-gray-900">
                        Net Cash from Operating Activities
                      </td>
                      <td className="px-6 py-4 text-sm text-right">
                        {renderAmount(reportData.operating_activities.net_cash)}
                      </td>
                    </tr>

                    {/* Investing Activities */}
                    <tr className="bg-blue-50">
                      <td colSpan={2} className="px-6 py-3 text-sm font-bold text-gray-900">
                        CASH FROM INVESTING ACTIVITIES
                      </td>
                    </tr>
                    {reportData.investing_activities.breakdown.map((item, idx) => (
                      <tr key={idx}>
                        <td className="px-6 py-3 text-sm text-gray-900 pl-10">{item.item}</td>
                        <td className="px-6 py-3 text-sm text-right">
                          {renderAmount(item.amount)}
                        </td>
                      </tr>
                    ))}
                    <tr className="font-bold bg-blue-50">
                      <td className="px-6 py-4 text-sm text-gray-900">
                        Net Cash from Investing Activities
                      </td>
                      <td className="px-6 py-4 text-sm text-right">
                        {renderAmount(reportData.investing_activities.net_cash)}
                      </td>
                    </tr>

                    {/* Financing Activities */}
                    <tr className="bg-purple-50">
                      <td colSpan={2} className="px-6 py-3 text-sm font-bold text-gray-900">
                        CASH FROM FINANCING ACTIVITIES
                      </td>
                    </tr>
                    {reportData.financing_activities.breakdown.map((item, idx) => (
                      <tr key={idx}>
                        <td className="px-6 py-3 text-sm text-gray-900 pl-10">{item.item}</td>
                        <td className="px-6 py-3 text-sm text-right">
                          {renderAmount(item.amount)}
                        </td>
                      </tr>
                    ))}
                    <tr className="font-bold bg-purple-50">
                      <td className="px-6 py-4 text-sm text-gray-900">
                        Net Cash from Financing Activities
                      </td>
                      <td className="px-6 py-4 text-sm text-right">
                        {renderAmount(reportData.financing_activities.net_cash)}
                      </td>
                    </tr>

                    {/* Net Change in Cash */}
                    <tr className="font-bold bg-emerald-50 border-t-2 border-gray-300">
                      <td className="px-6 py-4 text-base text-gray-900">NET CHANGE IN CASH</td>
                      <td className="px-6 py-4 text-base text-right">
                        {renderAmount(reportData.net_change_in_cash)}
                      </td>
                    </tr>

                    {/* Ending Cash */}
                    <tr className="font-bold bg-emerald-50">
                      <td className="px-6 py-4 text-base text-gray-900">ENDING CASH BALANCE</td>
                      <td className="px-6 py-4 text-base text-right text-emerald-600">
                        {formatCurrency(reportData.ending_cash)}
                      </td>
                    </tr>

                    {/* Reconciliation */}
                    {Math.abs(reportData.variance) > 0.01 && (
                      <tr className="bg-yellow-50">
                        <td className="px-6 py-3 text-sm text-gray-700 italic">
                          Reconciliation Variance (should be zero)
                        </td>
                        <td className="px-6 py-3 text-sm text-right text-yellow-800">
                          {formatCurrency(reportData.variance)}
                        </td>
                      </tr>
                    )}

                    <tr className="bg-gray-50">
                      <td className="px-6 py-3 text-sm text-gray-700 italic">
                        Actual Ending Cash (from Balance Sheet)
                      </td>
                      <td className="px-6 py-3 text-sm text-right text-gray-900">
                        {formatCurrency(reportData.actual_ending_cash)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Report Footer */}
            <div className="mt-6 text-sm text-gray-500 print:block">
              <p>Generated on {new Date().toLocaleString('en-NG')}</p>
              <p>Report run by: {user?.email}</p>
              <p className="text-emerald-600 font-medium">Wholesale Module Report</p>
            </div>
          </>
        )}

        {/* Print Styles */}
        <style>{`
          @media print {
            body * {
              visibility: hidden;
            }
            .p-4, .p-4 * {
              visibility: visible;
            }
            .print\\:hidden {
              display: none !important;
            }
            .print\\:block {
              display: block !important;
            }
          }
        `}</style>
      </div>
    </div>
  );
};

export default WholesaleCashFlowPage;
